import React, { useEffect, useState } from 'react';
import { Layers, Plus, Trash2, Pencil, X } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import { Button } from '../../components/ui/Button';
import api from '../../services/api';
import alertService from '../../utils/alert';

const ICON_OPTIONS = ['Plug', 'Building2', 'GraduationCap', 'ShoppingCart', 'Stethoscope', 'Scale', 'Landmark', 'Briefcase'];

const BLANK = {
  name: '', icon: 'Plug', group: '', listApiToolId: '', recordIdField: '',
  detailApiToolId: '', detailParamName: '', searchParamName: '', isActive: true,
};

export default function ExternalModulesAdminPage() {
  const [modules, setModules] = useState([]);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [modsRes, toolsRes] = await Promise.all([
        api.get('/support/external-modules'),
        api.get('/support/api-tools'),
      ]);
      setModules(modsRes.data.data || []);
      setTools(toolsRes.data.data || []);
    } catch (err) {
      alertService.toast('error', 'Failed to load external modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const listTools = tools.filter(t => t.resultMode === 'list');
  const objectTools = tools.filter(t => t.resultMode !== 'list');

  const save = async () => {
    if (!editing.name.trim() || !editing.group.trim() || !editing.listApiToolId) {
      return alertService.toast('error', 'Name, group, and a list API tool are required.');
    }
    setSaving(true);
    try {
      const payload = { ...editing };
      if (!payload.detailApiToolId) { payload.detailApiToolId = null; payload.detailParamName = ''; }
      if (editing._id) {
        await api.put(`/support/external-modules/${editing._id}`, payload);
      } else {
        await api.post('/support/external-modules', payload);
      }
      setEditing(null);
      load();
      alertService.toast('success', 'External module saved');
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/support/external-modules/${id}`);
      load();
    } catch (err) {
      alertService.toast('error', 'Failed to delete module');
    }
  };

  return (
    <DashboardModuleShell
      title="External Modules"
      subtitle="Turn any API Playground tool into a dedicated CRM page — one per vertical (fintech, school ERP, ecommerce, healthcare, legal, or anything else)."
      icon={Layers}
      actions={<Button onClick={() => setEditing({ ...BLANK })}><Plus className="w-4 h-4 mr-2" />New Module</Button>}
    >
      {listTools.length === 0 && !loading && (
        <div className="mb-6 p-4 bg-[#FEF1E5] border border-[#FDE4C9] rounded-md text-[12px] text-[#A96404]">
          No List-mode API tools exist yet. Go to <a href="/dashboard/support/api-playground" className="font-bold underline">API Playground</a> and create one with Result Mode set to "List of Records" before building a module here.
        </div>
      )}

      <div className="sf-card">
        {loading ? (
          <div className="text-center py-16 text-[13px] text-[#706E6B]">Loading…</div>
        ) : modules.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Layers className="w-10 h-10 text-[#D8DDE6] mx-auto mb-4" />
            <h3 className="text-[15px] font-bold text-[#080707] mb-2">No external modules yet</h3>
            <p className="text-[13px] text-[#706E6B]">Create one to get a dedicated page backed by a connected API.</p>
          </div>
        ) : (
          <div className="sf-table-container">
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Group</th>
                  <th>List Tool</th>
                  <th>Detail Tool</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modules.map(m => (
                  <tr key={m._id}>
                    <td className="text-[13px] font-bold text-[#080707]">{m.name}</td>
                    <td className="text-[12px] text-[#706E6B]">{m.group}</td>
                    <td className="text-[12px] text-[#706E6B] font-mono">{tools.find(t => t._id === m.listApiToolId)?.name || '—'}</td>
                    <td className="text-[12px] text-[#706E6B] font-mono">{tools.find(t => t._id === m.detailApiToolId)?.name || '—'}</td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a href={`/dashboard/external/${m._id}`} className="text-[11px] font-bold text-[#0176D3]">View Page</a>
                        <button onClick={() => setEditing({ ...BLANK, ...m })} className="text-[#706E6B] hover:text-[#0176D3]"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(m._id)} className="text-[#706E6B] hover:text-[#BA0517]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center sm:p-4 bg-black/40" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg p-4 sm:p-6 max-h-[85vh] overflow-auto rounded-t-lg sm:rounded-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">{editing._id ? 'Edit Module' : 'New Module'}</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input required placeholder="Name (e.g. Patients, Students, Cases)" value={editing.name}
                onChange={e => setEditing(p => ({ ...p, name: e.target.value }))}
                className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px]" />
              <input required placeholder="Group / vertical label (e.g. Healthcare)" value={editing.group}
                onChange={e => setEditing(p => ({ ...p, group: e.target.value }))}
                className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px]" />
              <select value={editing.icon} onChange={e => setEditing(p => ({ ...p, icon: e.target.value }))}
                className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px]">
                {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>

              <div>
                <label className="text-[12px] font-semibold text-[#706E6B] block mb-1">List API Tool *</label>
                <select value={editing.listApiToolId} onChange={e => setEditing(p => ({ ...p, listApiToolId: e.target.value }))}
                  className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="">Select a List-mode tool…</option>
                  {listTools.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>
              <input placeholder="Record ID field (a responseFields label from the list tool)" value={editing.recordIdField}
                onChange={e => setEditing(p => ({ ...p, recordIdField: e.target.value }))}
                className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] font-mono" />
              <input placeholder="Search param name (optional — a list tool query param)" value={editing.searchParamName}
                onChange={e => setEditing(p => ({ ...p, searchParamName: e.target.value }))}
                className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] font-mono" />

              <div className="pt-2 border-t border-[#D8DDE6]">
                <label className="text-[12px] font-semibold text-[#706E6B] block mb-1">Detail API Tool (optional)</label>
                <select value={editing.detailApiToolId || ''} onChange={e => setEditing(p => ({ ...p, detailApiToolId: e.target.value }))}
                  className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px]">
                  <option value="">None — no drill-down</option>
                  {objectTools.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                </select>
              </div>
              {editing.detailApiToolId && (
                <input placeholder="Detail tool param name to receive the record ID" value={editing.detailParamName}
                  onChange={e => setEditing(p => ({ ...p, detailParamName: e.target.value }))}
                  className="w-full px-4 h-10 bg-[#F4F6F9] border border-[#D8DDE6] rounded text-[13px] font-mono" />
              )}

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
                <Button onClick={save} loading={saving}>Save</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardModuleShell>
  );
}
